import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import z from 'zod'

import { useCreateNote } from '@/api/hooks/useCreateNote'
import PageHeader from '@/components/molecules/page-header'
import { Button } from '@/components/ui/button'
import { Field, FieldError } from '@/components/ui/field'
import { Textarea } from '@/components/ui/textarea'
import { useSubscription } from '@/contexts/subscription-context'
import { useIndividuals } from '@/hooks/useIndividuals'
import { useAppDispatch } from '@/redux/hooks'
import { AssistantActions } from '@/redux/reducers/assistant'

const schema = z.object({
  title: z.string().optional(),
  note: z.string().min(3),
})

type CreateNoteInput = z.input<typeof schema>

type Props = {
  /**
   * Set when this page was reached from an errand — attaches the note to that errand (same
   * pattern as `DocumentUploadPage`'s `errandId`) and returns there instead of the new note's own
   * page once saved.
   */
  errandId?: string
}

export const CreateNotePage = ({ errandId }: Props) => {
  const { selectedIndividualId } = useIndividuals()
  const { isReadOnly } = useSubscription()
  const { isPending, mutateAsync: createNote } = useCreateNote()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const form = useForm<CreateNoteInput>({
    defaultValues: { title: '', note: '' },
    resolver: zodResolver(schema),
  })

  const { ref: noteRef, ...noteRegister } = form.register('note')

  const autoResize = useCallback((el: HTMLTextAreaElement | null) => {
    noteRef(el)
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [noteRef])

  const onCancel = () => {
    if (errandId) {
      navigate({ params: { id: errandId }, to: '/errands/$id' })
      return
    }
    navigate({ to: '/notes' })
  }

  // Reachable by URL even with the create button disabled. The backend rejects the
  // write anyway; this avoids presenting a form that cannot be submitted. Leaves the
  // way it came, so an errand-scoped note returns to its errand.
  useEffect(() => {
    if (!isReadOnly) return
    if (errandId) {
      navigate({ params: { id: errandId }, replace: true, to: '/errands/$id' })
      return
    }
    navigate({ replace: true, to: '/notes' })
  }, [isReadOnly, errandId, navigate])

  const handleSubmit = async (values: CreateNoteInput) => {
    if (!selectedIndividualId) return

    // Generation (title + timeline events) kicks off in the background on the
    // server; the detail page shows a spinner until RTDB reports completion.
    const note = await createNote({
      individualId: selectedIndividualId,
      requestBody: { note: values.note, title: values.title?.trim() || undefined, errandId },
    })

    if (errandId) {
      // Chat shows a loading bubble until the backend's automated reaction lands — see
      // `pendingErrandReactionId` and `useErrandReactionRealtime`.
      dispatch(AssistantActions.setPendingErrandReaction(errandId))
      navigate({ params: { id: errandId }, to: '/errands/$id' })
      return
    }
    navigate({ params: { noteId: note.id }, to: '/notes/$noteId' })
  }

  return (
    <div className="space-y-8 z-10">
      <PageHeader
        body={t('notesPage.create.description')}
        heading={t('notesPage.create.title')}
      />
      <FormProvider {...form}>
        <form className="flex flex-col gap-4" onSubmit={form.handleSubmit(handleSubmit)}>
          <div className="rounded-lg border bg-layer-1 overflow-hidden">
            <div className="px-4 pt-4 pb-2">
              <Textarea
                {...form.register('title')}
                className="border-0 bg-transparent shadow-none font-semibold text-lg px-0 focus-visible:ring-0 resize-none overflow-hidden leading-snug min-h-0 placeholder:text-muted-foreground placeholder:font-semibold"
                onInput={e => {
                  const el = e.currentTarget
                  el.style.height = 'auto'
                  el.style.height = `${el.scrollHeight}px`
                }}
                rows={1}
                placeholder={t('notesPage.create.titlePlaceholder')}
              />
            </div>
            <Field>
              <Textarea
                {...noteRegister}
                className="border-0 rounded-none bg-transparent shadow-none resize-none overflow-hidden min-h-[120px] focus-visible:ring-0 px-4 py-3"
                onInput={e => {
                  const el = e.currentTarget
                  el.style.height = 'auto'
                  el.style.height = `${el.scrollHeight}px`
                }}
                placeholder={t('dialogs.createUpdateNote.inputPlaceholder')}
                ref={autoResize}
              />
              {form.formState.errors.note && (
                <FieldError className="px-4">{form.formState.errors.note.message}</FieldError>
              )}
            </Field>
            <div className="flex justify-end gap-2 px-4 pb-3">
              <Button onClick={onCancel} type="button" variant="outline">
                {t('global.cancel')}
              </Button>
              <Button disabled={isPending} type="submit">
                {t('global.save')}
              </Button>
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  )
}

export default CreateNotePage
